import { type JSX, type Ref, useEffect, useRef, useState } from 'react';
import { assetConstants, type ChainflipAsset } from '@chainflip/utils/chainflip';
import { create } from 'zustand';
import AddressInput from '@/shared/components/AddressInput';
import { DashboardModal } from '@/shared/components/dashboards/Modal';
import { Divider } from '@/shared/components/Divider';
import Button from '@/shared/components/flip-ui-kit/Button';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import { chainflipAssetMap } from '@/shared/utils/env';
import useRefundAddresses from 'packages/liquidity-provision/hooks/useRefundAddresses';
import useUpdateRefundAddress from 'packages/liquidity-provision/hooks/useUpdateRefundAddress';

interface RefundAddressModalStore {
  isOpen: boolean;
  asset: ChainflipAsset | undefined;
  setAssetAndOpen: (asset?: ChainflipAsset) => void;
  close: () => void;
}

export const useRefundAddressModalStore = create<RefundAddressModalStore>((set) => ({
  isOpen: false,
  asset: undefined,
  setAssetAndOpen: (asset) => set({ isOpen: true, asset }),
  close: () => set({ isOpen: false }),
}));

export const RefundAddressModalContent = ({
  onComplete,
  asset,
  onCancel,
  focusRef,
  open,
}: {
  onComplete: () => void;
  asset: ChainflipAsset | undefined;
  focusRef: Ref<HTMLButtonElement>;
  onCancel: () => void;
  open: boolean;
}) => {
  const refundAddresses = useRefundAddresses();
  const [address, setAddress] = useState('');
  const [addressInputError, setAddressInputError] = useState<string>();

  const handleAddressInput = (refundAddress: string) => {
    setAddressInputError(undefined);
    setAddress(refundAddress);
  };

  const { mutate: onSave, isPending } = useUpdateRefundAddress({
    chain: asset ? assetConstants[asset].chain : undefined,
    setAddressInputError,
    address,
    onComplete,
  });

  useEffect(() => {
    if (open) {
      setAddressInputError(undefined);
      if (asset) setAddress(refundAddresses?.[assetConstants[asset].chain] ?? '');
    }
  }, [open, asset]);

  return (
    <div className="flex max-h-[317px] w-[487px] flex-col gap-y-6 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-6">
      <div className="flex flex-col gap-y-3">
        <div className="flex items-center justify-between">
          <span className="font-aeonikMedium text-16 text-cf-white">
            Set {asset ? chainflipAssetMap[asset].chain.name : ''} refund address
          </span>
          <button type="button" className="text-cf-white" onClick={onCancel}>
            <CloseIcon />
          </button>
        </div>
        <div className="text-14 text-cf-light-2">
          You must provide a refund address for network security. You only need to do this once and
          can be changed anytime.
        </div>
      </div>
      <Divider className="h-px" />
      <AddressInput
        token={asset ? chainflipAssetMap[asset] : undefined}
        address={address}
        setAddress={handleAddressInput}
        error={addressInputError}
        placeholder="Refund Address"
      />
      <div className="flex justify-end space-x-2">
        <Button ref={focusRef} disabled={isPending} type="secondary-standard" onClick={onCancel}>
          Cancel
        </Button>
        <Button loading={isPending && 'iconOnly'} onClick={onSave}>
          Save
        </Button>
      </div>
    </div>
  );
};

export const SetRefundAddressModal = (): JSX.Element => {
  const focusRef = useRef<HTMLButtonElement>(null);
  const { asset, isOpen, close } = useRefundAddressModalStore();

  return (
    <DashboardModal open={isOpen} onClose={close} initialFocus={focusRef}>
      <RefundAddressModalContent
        asset={asset}
        onCancel={close}
        onComplete={close}
        focusRef={focusRef}
        open={isOpen}
      />
    </DashboardModal>
  );
};
